import { List, Typography } from 'antd';
import React, { forwardRef, memo, useEffect, useImperativeHandle } from 'react';
import { aiAgentCenterMessages } from 'messages';
import { useAIAgentCenterContext } from 'contexts/ai-agent-center';
import AIChatHistoryItem from './AIChatHistoryItem';
import { useLazyOmniChatSessionSupervisorHistories, useOmniChatSessionSupervisorHistoriesList } from 'hooks';
import { InView } from 'react-intersection-observer';
import dayjs from 'dayjs';
import { twMerge } from 'tailwind-merge';

type AIChatHistoryListProps = {};

export type AIChatHistoryListRefProps = {
  handleRefetchChatHistory: () => void;
};

const AIChatHistoryList = forwardRef<AIChatHistoryListRefProps, AIChatHistoryListProps>((props, ref) => {
  useImperativeHandle(ref, () => ({
    handleRefetchChatHistory: () => {
      handleFetchData();
    }
  }));

  const { chatSession, setChatSession, filterForm } = useAIAgentCenterContext();
  const { chatSource, dateRange, keywordChat } = filterForm || {};

  const {
    data: chatSessionSupervisorHistories,
    fetchData,
    count,
    isLoading,
    handleLoadMore,
    hasMore
  } = useLazyOmniChatSessionSupervisorHistories();

  useEffect(() => {
    handleFetchData();
  }, [chatSource, keywordChat, dateRange?.[0], dateRange?.[1]]);

  const handleFetchData = () => {
    fetchData({
      isHandleByAi: true,
      source: chatSource,
      fromStartTime: dateRange?.[0] ? dayjs(dateRange[0]).startOf('day').toISOString() : undefined,
      toEndTime: dateRange?.[1] ? dayjs(dateRange[1]).endOf('day').toISOString() : undefined,
      keyword: keywordChat ?? ''
    });
  };

  return (
    <div className='h-full max-h-full w-full overflow-y-auto'>
      <div className='flex h-10 items-center px-5'>
        <Typography.Text className='text-xs text-colorTextPlaceholder'>
          {aiAgentCenterMessages.chatCount(count)}
        </Typography.Text>
      </div>
      <List
        key={'chat-history-list'}
        className={twMerge('w-full pb-5')}
        loading={isLoading}
        dataSource={chatSessionSupervisorHistories}
        renderItem={(item) => (
          <AIChatHistoryItem
            item={item}
            selected={chatSession?.chatSessionId === item.chatSessionId}
            onSelect={() => {
              setChatSession(item);
            }}
          />
        )}
        rowKey={'chatSessionId'}
        footer={
          hasMore ? (
            <InView
              className='-mt-3'
              onChange={(open) => {
                if (open) {
                  handleLoadMore();
                }
              }}
            >
              <div className='flex h-18 gap-3 px-5 py-3'>
                <div>
                  <div className='skeleton-active h-12 w-12 rounded-full'></div>
                </div>
                <div className='mt-[3px] flex flex-1 flex-col gap-1'>
                  <div className='skeleton-active h-5 w-[180px] rounded-sm'></div>
                  <div className='skeleton-active h-[18px] w-[100px] rounded-sm'></div>
                </div>
                <div className='mt-[3px] flex h-full flex-col items-end gap-1.5'>
                  <div className='skeleton-active h-[14px] w-[40px] rounded-sm'></div>
                  <div className='skeleton-active h-[14px] w-[30px] rounded-sm'></div>
                </div>
              </div>
            </InView>
          ) : null
        }
      />
    </div>
  );
});

export default memo(AIChatHistoryList);
